import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import _isEqual from 'lodash-es/isEqual'

import * as button from '../../../button'
import * as text from '../../text'
import * as models from '../models'
import * as dsSingle from '../single'

import * as components from './components'
import { AtnDsSelectSingleEcStore } from './store'

/**
 * TODO: documentation
 */
@ngCore.Component({
  imports: [
    dsSingle.Component,
    button.Component,
    text.Component,
    components.addCustomLabelDialog.Component,
  ],
  providers: [AtnDsSelectSingleEcStore],
  selector: 'atn-ds-select-single-ec',
  standalone: true,
  styleUrls: ['./single-ec.component.scss'],
  templateUrl: './single-ec.component.html',
})
export class AtnDsSelectSingleEcComponent<
  TTypeParams extends ec.models.TypeParams.Constraint,
> {
  /** @see {@link AtnDsSelectSingleEcStore<TTypeParams>} */
  public readonly store = ngCore.inject<AtnDsSelectSingleEcStore<TTypeParams>>(
    AtnDsSelectSingleEcStore,
  )
  /**
   * Entity collection to use for the select
   *
   * Items from provided entity collection will be used to populate the select
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _ecSg = ngCore.input.required<ec.Collection<TTypeParams>>({
    alias: 'collection',
  })
  /** Label to display above the select */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })
  /** Whether the select is disabled */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _disabledSg = ngCore.input<boolean | undefined>(undefined, {
    alias: 'disabled',
  })
  /**
   * Configuration check {@link models.Config}
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _configSg = ngCore.input<models.Config | undefined>(
    undefined,
    {
      alias: 'config',
    },
  )
  /**
   * TODO: Better documentation.
   *
   * This conveys the "source" of the select to ngRx store.
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _descriptorSg = ngCore.input.required<string>({
    alias: 'descriptor',
  })
  /**
   * {@link ec.plugins.standard.association.models.Selection.Target Context}
   * for which this select is being used.
   */
  // NOTE: public due to the fact that it is used as ngCore.input
  public readonly _selectionTargetSg = ngCore.input<
    ec.models.Target | undefined
  >(undefined, {
    alias: 'selectionTarget',
  })
  /**
   * List of extra {@link models.Item} to place _before_ the entities
   *
   * @see {@link _appendSg}
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _prependSg = ngCore.input<readonly models.Item[]>([], {
    alias: 'prepend',
  })
  /**
   * List of extra {@link models.Item} to place _after_ the entities
   *
   * @see {@link _prependSg}
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _appendSg = ngCore.input<readonly models.Item[]>([], {
    alias: 'append',
  })

  /**
   * When given, instead of showing the current list of entities, the single
   * select will only contain entities from the requested group
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly _groupIdSg =
    ngCore.input<ec.plugins.standard.group.models.Group.Id>(undefined, {
      alias: 'group',
    })

  /**
   * When `true`, nothing will be shown in the UI if there are no items to show
   */
  // NOTE: public due to the fact that it is used as  ngCore.input
  public readonly hideOnEmptySg = ngCore.input<boolean>(false, {
    alias: 'hideOnEmpty',
  })

  /**
   * Whether the select is required or not.
   */
  // NOTE: public only for the purpose of ngCore.input
  public readonly requiredSg = ngCore.input(false, { alias: 'required' })

  /**
   * When `true`, nothing will be shown in the UI
   */
  public readonly hideSg = ngCore.computed(
    (hideOnEmpty = this.hideOnEmptySg(), items = this.store.itemsSg()) =>
      hideOnEmpty && items.length === 0,
  )

  constructor() {
    ngCore.effect(() => this.store.patchState({ ec: this._ecSg() }))
    ngCore.effect(() => this.store.patchState({ label: this._labelSg() }))
    ngCore.effect(() => this.store.patchState({ disabled: this._disabledSg() }))
    ngCore.effect(() =>
      this.store.patchState({ selectionTarget: this._selectionTargetSg() }),
    )
    ngCore.effect(() =>
      this.store.patchState({ descriptor: this._descriptorSg() }),
    )
    ngCore.effect(() => this.store.patchState({ config: this._configSg() }))
    ngCore.effect(() => this.store.patchState({ prepend: this._prependSg() }))
    ngCore.effect(() => this.store.patchState({ append: this._appendSg() }))
    ngCore.effect(() => this.store.patchState({ groupId: this._groupIdSg() }))
    ngCore.effect(() => this.store.patchState({ required: this.requiredSg() }))
  }
}
